/**
 * Every word the journey tracker says to a client. Calm, specific, second
 * person, no jargon without an explanation, no em dashes (tested). Kept apart
 * from journey.ts so it can move to a strings file for the SwiftUI app.
 *
 * Each status answers three questions: what this means, what happens next,
 * and who is doing it.
 */
import { friendlyDate, longDate, timeOfDay } from "@/lib/format";
import { brand } from "@/config/brand";
import type { Actor, ChapterKey, Journey, RequirementView, StatusKey, StepKey } from "./journey";

export interface CopyContext {
  /** First names. */
  strategist: string;
  coordinator: string;
  /** "F&G", or a neutral fallback when unknown. */
  carrier: string;
  product: string;
}

export interface Cta {
  label: string;
  to: string;
  /** A page outside the app (her booking link): opens in a new tab. */
  external?: boolean;
}

export interface StatusCopy {
  eyebrow: string;
  title: string;
  meaning: string;
  next: string;
  cta: Cta | null;
}

export const CHAPTER_LABEL: Record<ChapterKey, string> = {
  getting_started: "Getting started",
  your_plan: "Your plan",
  carrier_review: "Carrier review",
  your_policy: "Your policy",
};

export const STEP_LABEL: Record<StepKey, string> = {
  intro_call: "Intro call",
  strategy_session: "Strategy session",
  plan_presented: "Your plan",
  pre_application: "Pre-application",
  submitted: "Sent to the carrier",
  underwriting: "Underwriting",
  requirements: "Requirements",
  decision: "Decision",
  issued: "Policy issued",
  first_premium: "First premium",
  active: "Policy active",
  annual_review: "Annual review",
};

/** One line under each step in the full timeline. */
export function stepHint(step: StepKey, ctx: CopyContext): string {
  switch (step) {
    case "intro_call":
      return `A short call with ${ctx.coordinator} about your goals.`;
    case "strategy_session":
      return `${ctx.strategist} maps your income, taxes and options.`;
    case "plan_presented":
      return "Your strategy, numbers and illustration, in writing.";
    case "pre_application":
      return "About 15 minutes of questions for your application.";
    case "submitted":
      return `${ctx.strategist} sends your application to ${ctx.carrier}.`;
    case "underwriting":
      return `${ctx.carrier} reviews your health and finances.`;
    case "requirements":
      return "An exam, a phone interview or documents, if asked.";
    case "decision":
      return `${ctx.carrier} approves your policy and its price.`;
    case "issued":
      return `${ctx.carrier} issues the policy. You sign for it.`;
    case "first_premium":
      return "Your first payment puts the coverage in force.";
    case "active":
      return "Your policy is working for you.";
    case "annual_review":
      return `Once a year, ${ctx.strategist} reviews it with you.`;
  }
}

export function actorLabel(actor: Actor, ctx: CopyContext): string {
  switch (actor) {
    case "you":
      return "You're up";
    case "strategist":
      return `${ctx.strategist} is on it`;
    case "coordinator":
      return `${ctx.coordinator} is on it`;
    case "carrier":
      return `${ctx.carrier} is on it`;
    case "exam_company":
      return "The exam company has it";
  }
}

function monthYear(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** "tomorrow at 4:00 PM", "today at 9:00 AM", or "Friday, October 2 at 10:00 AM". */
export function when(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const day =
    d.toDateString() === now.toDateString()
      ? "today"
      : d.toDateString() === tomorrow.toDateString()
        ? "tomorrow"
        : d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  return `${day} at ${timeOfDay(iso)}`;
}

export function statusCopy(j: Journey, ctx: CopyContext): StatusCopy {
  const { strategist: s, coordinator: c, carrier } = ctx;
  const appt = j.nextAppointment;
  const open = j.requirements.filter((r) => r.state === "needed");
  const map: Record<StatusKey, () => StatusCopy> = {
    "intro.not_booked": () => ({
      eyebrow: "Getting started",
      title: "Let's find a time to talk",
      meaning: "Your first step is a short intro call about your goals. There's nothing to prepare.",
      next: `Reply in Messages and ${c} will book it with you.`,
      cta: { label: "Message us", to: "/messages" },
    }),
    "intro.booked": () => ({
      eyebrow: "Getting started",
      title: "Your intro call is booked",
      meaning: `${c} will call ${appt ? when(appt.startsAt) : "at the time you picked"}. It takes about 20 minutes, and there's nothing to prepare.`,
      next: `Next, you'll meet ${s}, your strategist, to map out your plan.`,
      cta: { label: "See the details", to: "/appointments" },
    }),
    "strategy.not_booked": () => ({
      eyebrow: "Getting started",
      title: "Next: your strategy session",
      meaning: `${s} will look at your income, savings and taxes, and show you what closes the gap.`,
      next: `${c} will help you pick a time. You can also reply in Messages.`,
      cta: { label: "Message us", to: "/messages" },
    }),
    "strategy.booked": () => ({
      eyebrow: "Getting started",
      title: "Your strategy session is booked",
      meaning: `${s} will walk through your retirement income, taxes and options with you ${appt ? when(appt.startsAt) : "soon"}.`,
      next: "Your written plan follows the session.",
      cta: { label: "See the details", to: "/appointments" },
    }),
    "plan.preparing": () => ({
      eyebrow: "Your plan",
      title: "Your plan is being prepared",
      meaning: `${s} is building your plan from what you shared.`,
      next: "It will show up here, with the carrier's illustration.",
      cta: null,
    }),
    "plan.ready": () => ({
      eyebrow: "Your plan",
      title: "Your plan is ready",
      meaning: "Your strategy, the numbers behind it and the carrier's illustration are in the Plan tab.",
      next: `Take your time with it. When you're ready, the next step is a short pre-application.`,
      cta: { label: "See your plan", to: "/plan" },
    }),
    "preapp.open": () => ({
      eyebrow: "Your plan",
      title: "Next: your pre-application",
      meaning: `About 15 minutes of health and financial questions so ${s} can prepare your application. ${s} sent you a secure link by email.`,
      next: appt && appt.kind === "app_call" ? `You can also finish it together on your application call, ${when(appt.startsAt)}.` : `Stuck on a question? Ask ${s} in Messages.`,
      cta: { label: "See your plan", to: "/plan" },
    }),
    "preapp.received": () => ({
      eyebrow: "Your plan",
      title: "Pre-application received",
      meaning: `Thank you. ${s} is preparing your ${carrier} application now.`,
      next: `You'll get an e-signature email from ${carrier} in a day or two.`,
      cta: null,
    }),
    submitted: () => ({
      eyebrow: "Carrier review",
      title: `Sent to ${carrier}`,
      meaning: `${carrier} has your application and is getting it ready for underwriting.`,
      next: "We'll update you here when underwriting starts.",
      cta: null,
    }),
    "underwriting.reviewing": () => ({
      eyebrow: "Carrier review",
      title: `${carrier} is reviewing your application`,
      meaning: "This is called underwriting: the carrier confirms your health and finances to set your final price.",
      next: `Underwriting can take several weeks. ${s} keeps an eye on it and will let you know if ${carrier} needs anything.`,
      cta: null,
    }),
    "underwriting.requirements_open": () => ({
      eyebrow: "Carrier review",
      title: open.length === 1 ? `${carrier} needs one thing from you` : `${carrier} needs ${open.length} things from you`,
      meaning: "Each one is explained below, with what to expect.",
      next: `Once they're in, ${carrier} can make a decision.`,
      cta: null,
    }),
    "underwriting.requirements_booked": () => ({
      eyebrow: "Carrier review",
      title: "You're all set",
      meaning: `Everything ${carrier} asked for is booked or done. The details are below.`,
      next: `After that, results go straight to ${carrier} for a decision.`,
      cta: null,
    }),
    "underwriting.waiting_records": () => ({
      eyebrow: "Carrier review",
      title: "Waiting on records",
      meaning: `${carrier} is waiting on records from your doctor or the lab. There's nothing for you to do.`,
      next: `Records can take a few weeks. ${s} keeps an eye on it.`,
      cta: null,
    }),
    "underwriting.hidden": () => ({
      eyebrow: "Carrier review",
      title: "Your application is with the carrier",
      meaning: "The carrier is reviewing your health and finances to set your final price.",
      next: `${s} keeps this updated and will let you know if anything is needed from you.`,
      cta: { label: "Message your strategist", to: "/messages" },
    }),
    "decision.approved": () => ({
      eyebrow: "Decision",
      title: "You're approved",
      meaning: `${carrier} approved your policy exactly as you applied.`,
      next: `${carrier} issues the policy next. Then you'll sign a delivery receipt and set up your first payment.`,
      cta: null,
    }),
    "decision.counteroffer": () => ({
      eyebrow: "Decision",
      title: `${carrier} approved you, with changes`,
      meaning: "This is called a counteroffer. It's common, and it's still an approval. What changed is below.",
      next: `${s} will walk you through it${appt ? ` ${when(appt.startsAt)}` : ""}. Nothing changes unless you say yes.`,
      cta: appt ? { label: "See your call", to: "/appointments" } : { label: `Message ${s}`, to: "/messages" },
    }),
    "decision.postponed": () => ({
      eyebrow: "Decision",
      title: `${carrier} asked to wait`,
      meaning: `${carrier} would like to look again${j.dates.postponedUntil ? ` after ${longDate(j.dates.postponedUntil)}` : " later"}. A postponement is not a decline.`,
      next: `${s} will talk you through your options in the meantime, including other carriers.`,
      cta: { label: `Message ${s}`, to: "/messages" },
    }),
    "decision.declined": () => ({
      eyebrow: "Decision",
      title: `${carrier} couldn't offer coverage`,
      meaning: `We're sorry. Every carrier looks at health differently, so this is often not the end of the road for your plan.`,
      next: `${s} will go over your options with you${appt ? ` ${when(appt.startsAt)}` : ""}. You can also ask ${carrier} for the reason in writing.`,
      cta: appt ? { label: "See your call", to: "/appointments" } : { label: `Message ${s}`, to: "/messages" },
    }),
    "ended.withdrawn": () => ({
      eyebrow: "Application",
      title: "Application withdrawn",
      meaning: `Your application was withdrawn${j.dates.decidedAt ? ` on ${longDate(j.dates.decidedAt)}` : ""}. Nothing else is needed from you.`,
      next: `Your plan stays here. Message ${s} whenever you'd like to pick it back up.`,
      cta: { label: "See your plan", to: "/plan" },
    }),
    "ended.not_taken": () => ({
      eyebrow: "Your policy",
      title: "You decided not to take the policy",
      meaning: `${carrier} has been told. If you paid anything, ${s} will confirm what happens to it.`,
      next: "Your plan stays here if you change your mind.",
      cta: { label: `Message ${s}`, to: "/messages" },
    }),
    "ended.free_look": () => ({
      eyebrow: "Your policy",
      title: "Policy cancelled during the free look",
      meaning: `You cancelled within the free look period, so ${carrier} returns what your contract says it will.`,
      next: `${s} will confirm when it's settled.`,
      cta: { label: `Message ${s}`, to: "/messages" },
    }),
    "issued.delivery": () => ({
      eyebrow: "Your policy",
      title: "Your policy is issued",
      meaning: `${carrier} issued your ${ctx.product}. Two last steps: sign your delivery receipt and make your first payment${j.dates.firstPremiumDue ? ` by ${friendlyDate(j.dates.firstPremiumDue)}` : ""}.`,
      next: "Your coverage is in force once the first payment is in.",
      cta: appt ? { label: "See your delivery call", to: "/appointments" } : null,
    }),
    "active.in_force": () => ({
      eyebrow: "Your policy",
      title: "Your policy is active",
      meaning: `Your ${ctx.product} with ${carrier} is in force and working for you.`,
      next: j.dates.reviewAround ? `Your first annual review with ${s} is around ${monthYear(j.dates.reviewAround)}.` : `${s} reviews it with you once a year.`,
      cta: { label: "See your policy", to: "/plan" },
    }),
    "active.review_booked": () => ({
      eyebrow: "Your policy",
      title: "Your annual review is coming up",
      meaning: `Once a year, ${s} checks how your policy is doing, what's changed in your life, and whether anything should change.`,
      next: appt ? `${when(appt.startsAt)}. Bring any questions.` : "Bring any questions.",
      cta: { label: "See the details", to: "/appointments" },
    }),
    "policy.lapsed": () => ({
      eyebrow: "Your policy",
      title: "Your policy needs attention",
      meaning: "A payment was missed and the policy has lapsed. It can often be reinstated.",
      next: `${s} will explain your options. The sooner the better.`,
      cta: { label: `Message ${s}`, to: "/messages" },
    }),
    "policy.surrendered": () => ({
      eyebrow: "Your policy",
      title: "Policy surrendered",
      meaning: "This policy has been surrendered and is no longer active.",
      next: `${s} is here if you'd like to talk about what's next.`,
      cta: null,
    }),
    "policy.matured": () => ({
      eyebrow: "Your policy",
      title: "Policy matured",
      meaning: "This policy reached the end of its term.",
      next: `${s} is here if you'd like to talk about what's next.`,
      cta: null,
    }),
    "policy.claim_paid": () => ({
      eyebrow: "Your policy",
      title: "Claim paid",
      meaning: "The claim on this policy has been paid.",
      next: `${s} is here for anything you need.`,
      cta: null,
    }),
    "missed.call": () =>
      j.recover?.bookUrl
        ? {
            eyebrow: "Getting started",
            title: j.recover.title || "Let's find a new time",
            meaning: "We missed you on the call. No problem, it happens.",
            next: "Pick a new time that works for you. It only takes a minute.",
            cta: { label: "Pick a new time", to: j.recover.bookUrl, external: true },
          }
        : {
            eyebrow: "Getting started",
            title: j.recover?.title || "We missed you on the call",
            meaning: j.recover ? "We missed you on the call. No problem, it happens." : "No problem, it happens.",
            next: `Message ${c} and they'll find a new time with you.`,
            cta: { label: `Message ${c === "our team" ? "us" : c}`, to: "/messages" },
          },
    "closed.not_a_fit": () => ({
      eyebrow: "Your file",
      title: "We can't offer a plan right now",
      meaning: "Based on what we've learned so far, we're not able to put a plan together for you at this time.",
      next: `If your situation changes, message ${c} and we'll take another look.`,
      cta: { label: "Message us", to: "/messages" },
    }),
    "closed.not_going_ahead": () => ({
      eyebrow: "Your plan",
      title: "You decided not to go ahead",
      meaning: "Thanks for letting us know. Your plan stays here if you'd like to look at it again.",
      next: `If anything changes, message ${s}.`,
      cta: { label: `Message ${s}`, to: "/messages" },
    }),
    "closed.do_not_contact": () => ({
      eyebrow: "Your file",
      title: "We won't reach out",
      meaning: "You asked us not to contact you, so we won't.",
      next: "If you change your mind, you can message us here any time.",
      cta: null,
    }),
    "closed.file": () => ({
      eyebrow: "Your file",
      title: "This file is closed",
      meaning: "",
      next: `For anything about this file, call our office at ${brand.supportPhone}.`,
      cta: null,
    }),
  };
  return map[j.status]();
}

/* ------------------------------------------------------------------ */
/* Requirements                                                        */
/* ------------------------------------------------------------------ */

export interface RequirementCopy {
  title: string;
  state: string;
  detail: string;
  how: string;
  tips: string[];
  tipsTitle: string;
}

const EXAM_TIPS = [
  "Fast for 8 hours if you can. Water is fine.",
  "Skip caffeine, alcohol and hard exercise for 24 hours.",
  "Have a photo ID and a list of your medications ready.",
];

export function requirementCopy(r: RequirementView, ctx: CopyContext): RequirementCopy {
  const done = r.state === "done";
  switch (r.kind) {
    case "exam":
      return {
        title: "Paramedical exam",
        state: done ? "Done" : r.state === "scheduled" ? `Booked for ${when(r.when)}` : "Needed",
        detail: "A licensed examiner comes to your home or office. About 20 to 30 minutes: height, weight, blood pressure, and a blood and urine sample. It's free.",
        how: r.state === "scheduled" ? "The examiner will text you before they arrive." : "The exam company will call you to book it, or reply in Messages and we'll book it for you.",
        tips: done ? [] : EXAM_TIPS,
        tipsTitle: "Before the exam",
      };
    case "phone_interview":
      return {
        title: `Phone interview with ${ctx.carrier}`,
        state: done ? "Done" : r.state === "scheduled" ? `Booked for ${when(r.when)}` : "Needed",
        detail: "15 to 30 minutes of health and lifestyle questions from the carrier's team.",
        how: r.state === "scheduled" ? `${ctx.carrier} will call you at that time.` : `${ctx.carrier}'s team will call you, or reply in Messages to pick a time.`,
        tips: done ? [] : ["Have your doctors' names and your medications handy."],
        tipsTitle: "Before the call",
      };
    case "documents":
      return {
        title: `Documents for ${ctx.carrier}`,
        state: done ? "All in" : `${r.items.filter((i) => !i.done).length || 1} to go${r.dueAt ? `, by ${friendlyDate(r.dueAt)}` : ""}`,
        detail: "Paperwork the carrier asked for to finish your file.",
        how: `Reply in Messages with a photo, or ${ctx.strategist} will send you an e-signature link.`,
        tips: [],
        tipsTitle: "",
      };
    case "doctor_records":
      return {
        title: "Your doctor's records",
        state: done ? "Received" : "Waiting",
        detail: `${ctx.carrier} asked your doctor's office for your records. This is called an APS (attending physician statement).`,
        how: `Nothing for you to do. Records can take a few weeks, and ${ctx.strategist} keeps an eye on it.`,
        tips: [],
        tipsTitle: "",
      };
    case "labs":
      return {
        title: "Lab results",
        state: done ? "Received" : "Waiting",
        detail: `The lab is sending your exam results to ${ctx.carrier}.`,
        how: `Nothing for you to do. Results go straight to ${ctx.carrier}.`,
        tips: [],
        tipsTitle: "",
      };
    case "carrier_items":
      return {
        title: `A few items for ${ctx.carrier}`,
        state: done ? "Done" : "Needed",
        detail: `${ctx.carrier} asked for a little more information.`,
        how: `${ctx.strategist} will tell you exactly what's needed.`,
        tips: [],
        tipsTitle: "",
      };
    case "first_premium":
      return {
        title: "First payment",
        state: done ? "Paid" : r.dueAt ? `Due ${friendlyDate(r.dueAt)}` : "Due soon",
        detail: "Your first premium puts the coverage in force.",
        how: `${ctx.strategist} will set it up with you and ${ctx.carrier}.`,
        tips: [],
        tipsTitle: "",
      };
  }
}
